/**
 * Locale URL groups for Ghost CMS pages that exist in multiple languages.
 *
 * Each entry maps locale codes to the canonical path for that page.
 * Used to generate hreflang <link> tags in the Head of each page.
 *
 * Locale codes:
 *   'en-US' → hreflang="en"  (also emitted as x-default)
 *   'fr'    → hreflang="fr"
 *   'es'    → hreflang="es"
 *   'de'    → hreflang="de"
 *
 * When adding a new multilingual page pair, add one object here.
 */
const LOCALE_URL_GROUPS = [
  // ── Homepage ────────────────────────────────────────────────────────────────
  {
    'en-US': '/',
    fr: '/fr/',
    es: '/es/',
    de: '/de/',
  },
  {
    'en-US': '/pricing/',
    fr: '/fr/tarification/',
    es: '/es/precios/',
    de: '/de/preise/',
  },
  {
    'en-US': '/about-us/',
    fr: '/fr/a-propos-de-nous/',
    es: '/es/sobre-nosotros/',
    de: '/de/uber-uns/',
  },
  {
    'en-US': '/faq/',
    fr: '/fr/faq/',
    es: '/es/faq/',
    de: '/de/faq/',
  },
  {
    'en-US': '/request-demo/',
    fr: '/fr/planifier-demo/',
    es: '/es/solicita-una-demo/',
    de: '/de/demo-anfordern/',
  },
  {
    'en-US': '/contact-us/',
    fr: '/fr/contactez-nous/',
    es: '/es/contacta-con-nosotros/',
    de: '/de/kontaktieren-sie-uns/',
  },
  {
    'en-US': '/partners/',
    fr: '/fr/partenaire-avec-nous/',
    es: '/es/socios/',
    de: '/de/partner/',
  },
  {
    'en-US': '/partnership/',
    fr: '/fr/partenariat/',
    es: '/es/colaboracion/',
    de: '/de/partnerschaft/',
  },
  {
    'en-US': '/slack-integration/',
    fr: '/fr/integration-slack/',
    es: '/es/slack-integration-es/',
    de: '/de/slack-integration-de/',
  },
  {
    'en-US': '/app-download/',
    fr: '/fr/telechargement-de-lapplication/',
    es: '/es/descargar-aplicacion/',
    de: '/de/app-herunterladen/',
  },
  {
    'en-US': '/virtual-marathon/',
    fr: '/fr/marathon-virtuel/',
    es: '/es/maraton-virtual/',
    de: '/de/virtueller-marathon/',
  },
  {
    'en-US': '/multi-activity-challenges/',
    fr: '/fr/defis-multi-activites/',
    es: '/es/desafios-multi-actividades/',
    de: '/de/multi-aktivitats-herausforderungen/',
  },
  {
    'en-US': '/steps-challenge/',
    fr: '/fr/etapes-defi/',
    es: '/es/desafio-de-pasos/',
    de: '/de/schritt-herausforderung/',
  },
  {
    'en-US': '/team-challenges/',
    fr: '/fr/defi-dequipe/',
    es: '/es/desafio-de-equipo/',
    de: '/de/team-herausforderung/',
  },
  {
    'en-US': '/remote-team-wellness/',
    fr: '/fr/bien-etre-de-lequipe-a-distance/',
    es: '/es/bienestar-del-equipo-remoto/',
    de: '/de/wellness-fur-remote-teams/',
  },
  {
    'en-US': '/global-employee-engagement/',
    fr: '/fr/engagement-mondial-des-employes/',
    es: '/es/compromiso-global-de-empleados/',
    de: '/de/globales-mitarbeiterengagement/',
  },
  {
    'en-US': '/mental-health-and-wellbeing-challenges/',
    fr: '/fr/sante-mentale-et-bien-etre/',
    es: '/es/salud-mental-y-bienestar/',
    de: '/de/mentale-gesundheit-und-wellbeing/',
  },
  {
    'en-US': '/holistic-wellness-program/',
    fr: '/fr/programme-de-mieux-etre-holistique/',
    es: '/es/programa-de-bienestar-holistico/',
    de: '/de/ganzheitliches-wellness-programm/',
  },
  {
    'en-US': '/wellness-rewards-program/',
    fr: '/fr/programme-de-recompenses-pour-le-mieux-etre/',
    es: '/es/programa-de-recompensas-para-el-bienestar/',
    de: '/de/wellness-belohnungsprogramm/',
  },
  {
    'en-US': '/health-fitness-analytics/',
    fr: '/fr/analyses-de-la-sante-et-de-la-condition-physique/',
    es: '/es/analisis-de-salud-y-condicion-fisica/',
    de: '/de/gesundheits-und-fitnessanalyse/',
  },
]

/** Maps internal locale codes to hreflang attribute values. */
const LOCALE_TO_HREFLANG = {
  'en-US': 'en',
  fr: 'fr',
  es: 'es',
  de: 'de',
}

const normalize = (p) => (p.endsWith('/') ? p : `${p}/`)

/**
 * Given a pathname, returns the locale group entry that contains it,
 * or null if this page has no known locale siblings.
 */
function getLocaleGroup(pathname) {
  const current = normalize(pathname)
  return (
    LOCALE_URL_GROUPS.find((group) =>
      Object.values(group).some((p) => normalize(p) === current)
    ) || null
  )
}

/**
 * Each feature page has a different slug per locale (Ghost CMS creates
 * separate pages per language with translated slugs).
 *
 * Each entry maps locale codes to the Ghost slug for that feature page.
 * getFeatureLocaleGroup() accepts ANY locale slug and resolves the full
 * hreflang group by looking up which entry the slug belongs to.
 */
const FEATURE_SLUG_GROUPS = [
  { 'en-US': 'admin-dashboard',                 fr: 'tableau-de-bord',                      es: 'tablero',                          de: 'admin-dashboard-de' },
  { 'en-US': 'engagement-tools',                fr: 'outils-dengagement',                   es: 'herramientas-de-compromiso',       de: 'engagement-werkzeuge' },
  { 'en-US': 'incentivization-and-rewards',     fr: 'lincitation-et-les-recompenses',       es: 'incentivos-y-recompensas',         de: 'anreize-und-belohnungen' },
  { 'en-US': 'security-and-compliance',         fr: 'securite-et-conformite',               es: 'seguridad-y-cumplimiento',         de: 'sicherheit-und-konformitat' },
  { 'en-US': 'accessibility',                   fr: 'accessibilite',                        es: 'accesibilidad',                    de: 'barrierefreiheit' },
  { 'en-US': 'integrations',                    fr: 'fr-integrations',                      es: 'integraciones',                    de: 'integrationen' },
  { 'en-US': 'activity-tracking',               fr: 'suivi-des-activites',                  es: 'seguimiento-de-actividades',       de: 'aktivitatsverfolgung' },
  { 'en-US': 'nutrition-and-hydration',         fr: 'nutrition-et-hydratation',             es: 'nutricion-e-hidratacion',          de: 'ernahrung-e-hidratacion' },
  { 'en-US': 'fitness-and-exercise',            fr: 'fitness-et-exercice',                  es: 'fitness-y-ejercicio',              de: 'fitness-und-bewegung' },
  { 'en-US': 'health-metrics',                  fr: 'mesure-de-la-sante',                   es: 'medicion-de-la-salud',             de: 'gesundheitsmessung' },
  { 'en-US': 'mental-health-and-mindfulness',   fr: 'bien-etre-mental',                     es: 'bienestar-mental',                 de: 'mentales-wohlbefinden' },
  { 'en-US': 'wellness-leagues',                fr: 'ligues-de-bien-etre',                  es: 'ligas-de-bienestar',               de: 'wellness-ligen' },
  { 'en-US': 'personalized-programs',           fr: 'programmes-personnalises',             es: 'programas-personalizados',         de: 'personalisierte-programme' },
  { 'en-US': 'health-data-upload',              fr: 'envoi-donnees-sante',                  es: 'subida-de-datos-de-salud',         de: 'gesundheitsdaten-upload' },
]

const FEATURE_PREFIXES = {
  'en-US': '/features/',
  fr: '/fr/caracteristiques/',
  es: '/es/caracteristicas/',
  de: '/de/merkmale/',
}

/**
 * Given any locale slug for a feature page, returns the full hreflang
 * group with correct paths for all four locales.
 */
function getFeatureLocaleGroup(slug) {
  const group = FEATURE_SLUG_GROUPS.find((g) => Object.values(g).includes(slug))
  if (!group) return null
  return Object.fromEntries(
    Object.entries(group).map(([locale, localSlug]) => [locale, `${FEATURE_PREFIXES[locale]}${localSlug}/`])
  )
}

module.exports = { LOCALE_URL_GROUPS, LOCALE_TO_HREFLANG, getLocaleGroup, getFeatureLocaleGroup }
